/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import React from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { When } from "react-if";
import { FlexContainer, Box, theme } from "@nordcloud/gnui";
import { ACTIONS, Can } from "~/services/auth";
import { WorkflowFields } from "../AddWorkflow/constants";
import { WorkflowSidebars } from "../constants";
import { WorkflowFormData } from "../types";
import {
  SchedulerSettingsForm,
  ServiceNowSettingsForm,
  WorkflowCanvas,
  BudgetsSettingsForm,
  KpiSettingsForm,
  mapSelectDataComponent,
  mapSendDataComponents,
  mapTriggerComponents,
  AppsEnvsSettingsForm,
  SlackSettingsForm,
  AppsEnvsOrgsSettingsForm,
} from "../WorkflowCanvas";
import { EmailSettingsForm } from "../WorkflowCanvas/EmailSettingsForm/EmailSettingsForm";
import { WorkflowProps } from "../WorkflowPropsMapping";
import { EditFormControls } from "./components/EditFormControls";
import { FlowComponents } from "./components/FlowComponents";

type Props = {
  workflow: WorkflowProps;
  toggleEdit: (newState: boolean) => void;
  isEditable: boolean;
  onCancel: () => void;
  requireSlackToken: boolean;
  requireServiceNowPassword: boolean;
};

export function WorkflowEditView({
  toggleEdit,
  isEditable,
  onCancel,
  workflow,
  requireSlackToken,
  requireServiceNowPassword,
}: Props) {
  const [openSidebar, setOpenSidebar] = React.useState<WorkflowSidebars>(
    WorkflowSidebars.NONE
  );
  const { watch } = useFormContext<WorkflowFormData>();
  const { dirtyFields } = useFormState<WorkflowFormData>();

  const isDirty = FieldsToMakeFormDirty.some((field) =>
    Object.hasOwn(dirtyFields, field)
  );
  const closeSidebar = () => setOpenSidebar(WorkflowSidebars.NONE);

  const [
    sendDataComponents,
    selectDataComponent,
    triggerComponents,
    anomalyCostsOptions,
  ] = watch([
    "sendDataComponents",
    "selectDataComponent",
    "triggerComponents",
    "anomalyCostsOptions",
  ]);

  return (
    <>
      <Can action={ACTIONS.updateWorkflow}>
        <Box
          mb={theme.spacing.spacing04}
          backgroundColor={theme.color.background.ui03}
          border="disabled"
        >
          <EditFormControls
            toggleEdit={(newState) => toggleEdit(newState)}
            isEditable={isEditable}
            isDirty={isDirty}
            onCancel={() => {
              onCancel();
              toggleEdit(false);
            }}
          />
        </Box>
      </Can>
      <When condition={isEditable}>
        <FlexContainer alignItems="flex-start">
          <FlowComponents />
          <WorkflowCanvas
            formMode="edit"
            title="Workflow Canvas"
            triggers={mapTriggerComponents({
              setOpenSidebar,
              types: triggerComponents,
              workflow,
            })}
            selectDataComponent={mapSelectDataComponent({
              type: selectDataComponent,
              setOpenSidebar,
              workflow,
            })}
            sendDataComponents={mapSendDataComponents({
              workflow,
              setOpenSidebar,
              types: sendDataComponents,
            })}
          />
        </FlexContainer>
        <SchedulerSettingsForm
          isOpen={openSidebar === "scheduler"}
          formMode="edit"
          onClose={closeSidebar}
          onSave={closeSidebar}
        />
        <ServiceNowSettingsForm
          isOpen={openSidebar === "serviceNow"}
          formMode="edit"
          requirePassword={requireServiceNowPassword}
          workflow={workflow}
          onClose={closeSidebar}
          onSave={closeSidebar}
        />
        <EmailSettingsForm
          isOpen={openSidebar === "email"}
          workflow={workflow}
          formMode="edit"
          onClose={closeSidebar}
          onSave={closeSidebar}
        />
        <SlackSettingsForm
          isOpen={openSidebar === "slack"}
          formMode="edit"
          requireToken={requireSlackToken}
          workflow={workflow}
          onClose={closeSidebar}
          onSave={closeSidebar}
        />
        <AppsEnvsOrgsSettingsForm
          isOpen={openSidebar === "savingsSuggestions"}
          formMode="edit"
          workflow={workflow}
          type="savingsSuggestions"
          onClose={closeSidebar}
        />
        <AppsEnvsOrgsSettingsForm
          isOpen={openSidebar === "costs"}
          formMode="edit"
          workflow={workflow}
          type="costsOptions"
          onClose={closeSidebar}
        />
        <AppsEnvsSettingsForm
          type="anomalyCostsOptions"
          formMode="edit"
          isOpen={openSidebar === "anomalyCosts"}
          applications={anomalyCostsOptions.applications ?? []}
          environments={[]}
          onClose={closeSidebar}
        />
        <KpiSettingsForm
          isOpen={openSidebar === "kpi"}
          onClose={closeSidebar}
        />
        <BudgetsSettingsForm
          isOpen={openSidebar === "budgets"}
          formMode="edit"
          workflow={workflow}
          onClose={closeSidebar}
        />
      </When>
    </>
  );
}

const FieldsToMakeFormDirty = [
  WorkflowFields.SCHEDULER_SUBMISSION_SNAPSHOT,
  WorkflowFields.SERVICE_NOW_SUBMISSION_SNAPSHOT,
  WorkflowFields.EMAIL_SUBMISSION_SNAPSHOT,
  WorkflowFields.SLACK_SUBMISSION_SNAPSHOT,
  WorkflowFields.TRIGGER_COMPONENTS,
  WorkflowFields.SELECT_DATA_COMPONENT,
  WorkflowFields.SEND_DATA_COMPONENTS,
  WorkflowFields.SAVINGS_SUGGESTIONS,
  WorkflowFields.KPI_OPTIONS,
  WorkflowFields.COSTS_OPTIONS,
  WorkflowFields.BUDGETS_OPTIONS,
  WorkflowFields.BUDGETS_SNAPSHOT,
  WorkflowFields.ANOMALY_COSTS_OPTIONS,
  WorkflowFields.ANOMALY_SNAPSHOT,
];
